import React, { useEffect, useState } from 'react';
import { trackCTAClick } from 'utils/analytics';
import GoogleLoginButton from 'components/shared/GoogleLoginButton';

const LoginModal = ({ isOpen, onClose, translations, onLoginSuccess, teamName = null, initialState = 'login' }) => {
  const [csrfToken, setCsrfToken] = useState('');
  const [modalState, setModalState] = useState(initialState); // login | register | forgot_password
  const emailAuthEnabled = true;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const t = translations.login_modal || {};

  useEffect(() => {
    const token = document.querySelector('[name="csrf-token"]')?.content;
    if (token) setCsrfToken(token);
  }, []);

  // Reset form when modal opens or state changes
  useEffect(() => {
    if (isOpen) {
      setErrors([]);
      setIsLoading(false);
      setResetSent(false);
    }
  }, [isOpen, modalState]);

  // Reset to initialState when modal closes
  useEffect(() => {
    if (!isOpen) {
      setModalState(initialState);
      setEmail('');
      setPassword('');
      setName('');
      setRole('');
      setErrors([]);
      setResetSent(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          active.blur(); // First ESC: dismiss autocomplete / unfocus input
          return;
        }
        onClose(); // Second ESC: close modal
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const jsonFetch = async (url, body) => {
    const token = document.querySelector('[name="csrf-token"]')?.content || csrfToken;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return { success: false, errors: [t.login_error || 'Something went wrong. Please try again.'] };
    }
    return response.json();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);
    try {
      const data = await jsonFetch('/users/sign_in.json', { user: { email, password } });
      if (data.success) {
        if (onLoginSuccess) {
          window.location.reload();
        } else {
          window.location.href = data.redirect_to;
        }
      } else if (data.unconfirmed) {
        // Show resend confirmation option
        setModalState('unconfirmed');
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrors([]);

    if (!role) {
      setErrors([t.role_required || 'Please select your role']);
      return;
    }
    if (password.length < 6) {
      setErrors([t.password_too_short || 'Password must be at least 6 characters']);
      return;
    }

    setIsLoading(true);
    try {
      const data = await jsonFetch('/users.json', { user: { name, email, password, role } });
      if (data.success) {
        if (data.confirmation_required) {
          // Email confirmation needed — show confirmation sent message
          setModalState('confirmation_sent');
        } else if (onLoginSuccess) {
          window.location.reload();
        } else {
          window.location.href = data.redirect_to;
        }
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setErrors([]);
    setIsLoading(true);
    try {
      const data = await jsonFetch('/users/confirmation.json', { user: { email } });
      if (data.success) {
        setResetSent(true); // reuse resetSent to show success message
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);
    try {
      const data = await jsonFetch('/users/password.json', { user: { email } });
      if (data.success) {
        setResetSent(true);
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const switchState = (newState) => {
    setModalState(newState);
    setErrors([]);
    setResetSent(false);
  };

  // Google OAuth buttons (shared between login and register states)
  const GoogleButtons = () => (
    <div className="space-y-4">
      <GoogleLoginButton
        role="teacher"
        label={`👨‍🏫 ${t.teacher_button || "I'm a Teacher"}`}
        onSubmit={() => trackCTAClick('teacher_signup', 'login_modal_teacher', '/users/auth/google_oauth2?role=teacher')}
        translations={t}
        showWarning={false}
      />
      <GoogleLoginButton
        role="student"
        label={`👨‍🎓 ${t.student_button || "I'm a Student"}`}
        onSubmit={() => trackCTAClick('student_signup', 'login_modal_student', '/users/auth/google_oauth2?role=student')}
        translations={t}
      />
    </div>
  );

  const TermsConsent = () => (
    <p className="text-xs text-gray-400 text-center">
      {t.terms_consent || 'By signing up, you agree to our'}{' '}
      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline">
        {t.terms_link || 'Terms of Service'}
      </a>
      {' '}{t.terms_consent_and || 'and'}{' '}
      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline">
        {t.privacy_link || 'Privacy Policy'}
      </a>
    </p>
  );

  const Divider = () => (
    <div className="flex items-center my-4">
      <div className="flex-1 border-t border-gray-200"></div>
      <span className="px-3 text-sm text-gray-400">{t.or_divider || 'or'}</span>
      <div className="flex-1 border-t border-gray-200"></div>
    </div>
  );

  const ErrorMessages = () => errors.length > 0 ? (
    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
      {errors.map((err, i) => (
        <p key={i} className="text-sm text-red-700">{err}</p>
      ))}
    </div>
  ) : null;

  const getTitle = () => {
    if (teamName) {
      const joinTitle = t.join_team_title || 'Join %{team_name}';
      return joinTitle.replace('%{team_name}', teamName);
    }
    if (!emailAuthEnabled) return t.title || 'Choose Your Role';
    if (modalState === 'register') return t.register_title || 'Create Account';
    if (modalState === 'forgot_password') return t.reset_password_title || 'Reset Password';
    if (modalState === 'confirmation_sent') return t.confirmation_sent || 'Check your email';
    if (modalState === 'unconfirmed') return t.unconfirmed_title || 'Email not verified';
    return t.login_title || 'Log In';
  };

  const getSubtitle = () => {
    if (!emailAuthEnabled) return t.subtitle || 'Please select how you would like to sign in';
    if (modalState === 'register') return t.register_subtitle || 'Choose your role to get started';
    if (modalState === 'forgot_password') return t.reset_password_subtitle || "Enter your email and we'll send you a reset link";
    if (modalState === 'confirmation_sent') return '';
    if (modalState === 'unconfirmed') return '';
    return t.login_subtitle || 'Sign in to your account';
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile header: back arrow */}
        <div className="lg:hidden px-4 py-3 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label={t.close || 'Close'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-semibold text-gray-900">{getTitle()}</span>
        </div>

        {/* Desktop header: blue bar */}
        <div className="hidden lg:flex px-6 py-4 border-b flex-shrink-0 items-center justify-between" style={{ backgroundColor: '#E6F0FF' }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#3366FF' }}>
              {getTitle()}
            </h2>
            <p className="text-sm mt-1" style={{ color: '#5588FF' }}>
              {getSubtitle()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label={t.close || 'Close'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* ===== LOGIN STATE ===== */}
          {modalState === 'login' && (
            <div className="space-y-4">
              <GoogleButtons />
              <TermsConsent />

              {emailAuthEnabled && (
                <>
                  <Divider />

                  <form onSubmit={handleLogin} className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.email_label || 'Email'}</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t.email_placeholder || 'your@email.com'}
                        required
                        autoComplete="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.password_label || 'Password'}</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t.password_placeholder || 'Enter password'}
                        required
                        autoComplete="current-password"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      />
                    </div>

                    <ErrorMessages />

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#3366FF' }}
                    >
                      {isLoading ? '...' : (t.login_button || 'Log In')}
                    </button>
                  </form>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={() => switchState('forgot_password')}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      {t.forgot_password || 'Forgot password?'}
                    </button>
                  </div>

                  <div className="text-center text-sm text-gray-600">
                    {t.no_account || "Don't have an account?"}{' '}
                    <button
                      type="button"
                      onClick={() => switchState('register')}
                      className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                      {t.signup_link || 'Sign up'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== REGISTER STATE (email auth only) ===== */}
          {emailAuthEnabled && modalState === 'register' && (
            <div className="space-y-4">
              <GoogleButtons />
              <Divider />

              <form onSubmit={handleRegister} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.name_label || 'Name'}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.name_placeholder || 'Your name'}
                    required
                    autoComplete="name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.email_label || 'Email'}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.email_placeholder || 'your@email.com'}
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.password_label || 'Password'}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.password_placeholder || 'Enter password'}
                    required
                    autoComplete="new-password"
                    minLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                  <p className="mt-1 text-xs text-gray-400">{t.password_min_length || 'At least 6 characters'}</p>
                </div>

                {/* Role selection — toggle pills */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.role_selection_label || 'I am a...'}</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRole('teacher')}
                      className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                        role === 'teacher'
                          ? 'border-[#3366FF] bg-[#E6F0FF] text-[#3366FF]'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-lg mb-1">👨‍🏫</div>
                      <div className="font-medium text-sm">{t.role_teacher || 'Teacher'}</div>
                      <div className="text-xs mt-0.5 opacity-70">{t.teacher_role_desc || 'Manage classes & students'}</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole('student')}
                      className={`p-3 rounded-xl border-2 text-center transition-all duration-200 ${
                        role === 'student'
                          ? 'border-[#3366FF] bg-[#E6F0FF] text-[#3366FF]'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-lg mb-1">👨‍🎓</div>
                      <div className="font-medium text-sm">{t.role_student || 'Student'}</div>
                      <div className="text-xs mt-0.5 opacity-70">{t.student_role_desc || 'Find & book tutors'}</div>
                    </button>
                  </div>
                </div>

                <ErrorMessages />

                <button
                  type="submit"
                  disabled={isLoading || !role}
                  className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#3366FF' }}
                >
                  {isLoading ? '...' : (t.signup_button || 'Sign Up')}
                </button>

                <TermsConsent />
              </form>

              <div className="text-center text-sm text-gray-600">
                {t.has_account || 'Already have an account?'}{' '}
                <button
                  type="button"
                  onClick={() => switchState('login')}
                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {t.login_link || 'Log in'}
                </button>
              </div>
            </div>
          )}

          {/* ===== FORGOT PASSWORD STATE (email auth only) ===== */}
          {emailAuthEnabled && modalState === 'forgot_password' && (
            <div className="space-y-4">
              {resetSent ? (
                <div className="text-center py-4">
                  <div className="text-4xl mb-3">✉️</div>
                  <p className="text-gray-700">
                    {t.reset_email_sent || "If this email is registered, you'll receive a password reset link"}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.email_label || 'Email'}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.email_placeholder || 'your@email.com'}
                      required
                      autoFocus
                      autoComplete="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    />
                  </div>

                  <ErrorMessages />

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#3366FF' }}
                  >
                    {isLoading ? '...' : (t.send_reset_link || 'Send Reset Link')}
                  </button>
                </form>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => switchState('login')}
                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {t.back_to_login || 'Back to login'}
                </button>
              </div>
            </div>
          )}

          {/* ===== UNCONFIRMED STATE ===== */}
          {modalState === 'unconfirmed' && (
            <div className="text-center py-6 space-y-4">
              {resetSent ? (
                <>
                  <div className="text-5xl">✉️</div>
                  <p className="text-gray-700 font-medium">
                    {t.confirmation_resent || 'Verification email resent!'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t.confirmation_sent_detail || 'Click the link in the email to activate your account'}
                  </p>
                </>
              ) : (
                <>
                  <div className="text-5xl">⚠️</div>
                  <p className="text-gray-700 font-medium">
                    {t.unconfirmed_error || 'Your email has not been verified yet'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t.unconfirmed_hint || 'Check your inbox for the verification email, or resend it below'}
                  </p>

                  <ErrorMessages />

                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={isLoading}
                    className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#3366FF' }}
                  >
                    {isLoading ? '...' : (t.resend_confirmation || 'Resend verification email')}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => switchState('login')}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                {t.back_to_login || 'Back to login'}
              </button>
            </div>
          )}

          {/* ===== CONFIRMATION SENT STATE ===== */}
          {modalState === 'confirmation_sent' && (
            <div className="text-center py-6 space-y-4">
              <div className="text-5xl">✉️</div>
              <p className="text-gray-700 font-medium">
                {t.confirmation_sent || 'Verification email sent — please check your inbox'}
              </p>
              <p className="text-sm text-gray-500">
                {t.confirmation_sent_detail || 'Click the link in the email to activate your account'}
              </p>
              <button
                type="button"
                onClick={() => switchState('login')}
                className="mt-4 px-6 py-2 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: '#3366FF' }}
              >
                {t.back_to_login || 'Back to login'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
